import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Keyboard } from 'swiper/modules';
import { FaPhoneAlt } from 'react-icons/fa';
import { formatUSPhone } from '../../utils/phoneUtils';
import type { Stylist } from './types';

interface StylistGridProps {
  stylists: Stylist[];
}

const StylistGrid: React.FC<StylistGridProps> = ({ stylists }) => {
  return (
    <section className="w-full" aria-label="Stylists">
      <Swiper
        modules={[FreeMode, Keyboard]}
        freeMode={{ enabled: true, momentum: true }}
        keyboard={{ enabled: true }}
        slidesPerView="auto"
        spaceBetween={10}
        grabCursor
        className="px-2 sm:px-3"
        breakpoints={{
          480: { spaceBetween: 12 },
          768: { spaceBetween: 16 },
          1024: { spaceBetween: 20 },
        }}
      >
        {stylists.map((stylist) => (
          <SwiperSlide
            key={stylist.id}
            className="!w-[78vw] sm:!w-[300px] md:!w-[340px] lg:!w-[380px]"
          >
            <article className="group relative h-full overflow-hidden rounded-2xl bg-white dark:bg-zinc-900 shadow-md transition active:scale-[0.98] hover:shadow-xl">
              {/* IMAGE */}
              <div className="relative aspect-[3/4] sm:aspect-[4/5] overflow-hidden">
                <img
                  src={stylist.image}
                  alt={`${stylist.name} - ${stylist.role}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>

              {/* CONTENT */}
              <div className="flex flex-col p-3 sm:p-4 md:p-5">
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white group-hover:text-primary transition-colors">
                  {stylist.name}
                </h3>

                <p className="mt-1 text-xs sm:text-sm md:text-base font-medium text-primary">
                  {stylist.role}
                </p>

                {/* CTA */}
                {stylist.phone && (
                  <a
                    href={formatUSPhone(stylist.phone)}
                    className="mt-3 sm:mt-4 flex items-center justify-center gap-2 rounded-lg sm:rounded-xl bg-primary px-3 sm:px-4 py-2.5 sm:py-3 text-white font-medium text-sm sm:text-base transition hover:bg-rose-600 active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-primary"
                    aria-label={`Call ${stylist.name}`}
                  >
                    <FaPhoneAlt className="text-xs sm:text-sm" />
                    <span className="truncate">
                      {/* Default: Book Now */}
                      <span className="group-hover:hidden">Book Now</span>
                      {/* Card Hover: Call + Number */}
                      <span className="hidden group-hover:inline">{stylist.phone}</span>
                    </span>
                  </a>
                )}
              </div>
            </article>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
};

export default StylistGrid;
